/**
 * ScrollArea — shadcn-style wrapper around Radix ScrollArea.
 *
 * Used by the workspace shell for both scrollable regions:
 *   - Sidebar nav (between top org-switch and bottom foot-card)
 *   - Main content area
 *
 * The scrollbar uses Flint's hairline tokens (line / line-2) so the chrome
 * stays visually quiet — the scrollbar is a subtle affordance, not a
 * design element. Hover-only thumb opacity matches the prototype.
 *
 * @see prototype/home/dashboard.html — sidebar + main both have native scroll
 *      with `overflow: auto`. We swap to Radix ScrollArea for parity
 *      across browsers (native macOS scrollbars vanish to 0px when
 *      mouse-tracking is off; Radix renders a real overlay so the
 *      affordance is always discoverable).
 */
import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { cn } from "../lib/cn.js";

export const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    /** Extra class for the inner Viewport (the thing the user scrolls). */
    viewportClassName?: string;
    /** Hide the scrollbar entirely — useful for tightly-controlled rails. */
    hideScrollbar?: boolean;
    /** Scrollbar orientation; defaults to vertical. */
    orientation?: "vertical" | "horizontal";
  }
>(function ScrollArea(
  { className, viewportClassName, hideScrollbar, orientation = "vertical", children, ...props },
  ref,
) {
  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        className={cn("h-full w-full rounded-[inherit]", viewportClassName)}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {hideScrollbar ? null : <ScrollBar orientation={orientation} />}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});

export const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(function ScrollBar({ className, orientation = "vertical", ...props }, ref) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      className={cn(
        "flex touch-none select-none transition-colors",
        // Vertical: hairline rail along the right edge. Horizontal: along
        // the bottom edge. Padding gives the thumb breathing room from the
        // viewport content underneath.
        orientation === "vertical" && "h-full w-2 border-l border-l-transparent p-[1px]",
        orientation === "horizontal" && "h-2 flex-col border-t border-t-transparent p-[1px]",
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        // `bg-line-2` is Flint's "secondary line" token — a hair darker than
        // `bg-line`. Hover bumps the thumb to `bg-muted` so the affordance
        // becomes more visible the moment the user reaches for it.
        className="relative flex-1 rounded-full bg-line-2 hover:bg-muted transition-colors"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
});
